import { createSlice } from "@reduxjs/toolkit";
import {
    getAllAssetsByCaseIdAction,
    getAssetByIdAction,
    createAssetAction,
    updateAssetAction,
    deleteAssetAction,
    searchAssetsAction,
    clearOtherDetailsStateAction,
    setSelectedAssetAction
} from "../../actions/Case/caseAssetOtherDetailsAction";

const initialState = {
    assets: [],
    parties: [],
    selectedAsset: null,
    loading: false,
    error: null,
    success: false,
    message: ""
};

const otherDetailsSlice = createSlice({
    name: "otherDetails",
    initialState,
    reducers: {
        clearOtherDetailsError: (state) => {
            state.error = null;
            state.message = "";
        },
        clearSelectedAsset: (state) => {
            state.selectedAsset = null;
        }
    },
    extraReducers: (builder) => {
        // =========== GET ALL ASSETS BY CASE ID ===========
        builder
            .addCase(getAllAssetsByCaseIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAllAssetsByCaseIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.assets = action.payload.assets;
                state.parties = action.payload.parties;
                state.message = action.payload.message;
            })
            .addCase(getAllAssetsByCaseIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch assets";
                state.assets = [];
            })

        // =========== GET SINGLE ASSET BY ID ===========
        builder
            .addCase(getAssetByIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAssetByIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.selectedAsset = action.payload.asset;
                state.message = action.payload.message;
            })
            .addCase(getAssetByIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch asset";
                state.selectedAsset = null;
            })

        // =========== CREATE NEW ASSET ===========
        builder
            .addCase(createAssetAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createAssetAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.assets.unshift(action.payload.asset);
                state.message = action.payload.message;
            })
            .addCase(createAssetAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to create asset";
            })

        // =========== UPDATE ASSET ===========
        builder
            .addCase(updateAssetAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateAssetAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.assets.findIndex(
                    a => a.id === action.payload.id
                );
                if (index !== -1) {
                    state.assets[index] = action.payload.asset;
                }

                if (state.selectedAsset?.id === action.payload.id) {
                    state.selectedAsset = action.payload.asset;
                }
                state.message = action.payload.message;
            })
            .addCase(updateAssetAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update asset";
            })

        // =========== DELETE ASSET ===========
        builder
            .addCase(deleteAssetAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteAssetAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.assets = state.assets.filter(
                    a => a.id !== action.payload.id
                );

                if (state.selectedAsset?.id === action.payload.id) {
                    state.selectedAsset = null;
                }
                state.message = action.payload.message;
            })
            .addCase(deleteAssetAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to delete asset";
            })

        // =========== SEARCH ASSETS ===========
        builder
            .addCase(searchAssetsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(searchAssetsAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.assets = action.payload.assets;
                state.message = action.payload.message;
            })
            .addCase(searchAssetsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to search assets";
                state.assets = [];
            })

        // =========== MATCHERS ===========
        builder
            .addMatcher(
                (action) => action.type === clearOtherDetailsStateAction.type,
                () => initialState
            )
            .addMatcher(
                (action) => action.type === setSelectedAssetAction.type,
                (state, action) => {
                    state.selectedAsset = action.payload;
                }
            );
    }
});

export const {
    clearOtherDetailsError,
    clearSelectedAsset
} = otherDetailsSlice.actions;

export default otherDetailsSlice.reducer;
